// src/components/MfaChallengeDialog.js
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Alert, CircularProgress, Box
} from '@mui/material';
import { TotpMultiFactorGenerator } from 'firebase/auth';

function MfaChallengeDialog({ open, resolver, onSuccess, onCancel }) {
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const handleVerify = async () => {
    if (otpCode.length !== 6) {
      setError('Please enter a 6-digit code.');
      return;
    }

    setError(null);
    setVerifying(true);

    try {
      const hint = resolver.hints[0];
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, otpCode);
      const userCredential = await resolver.resolveSignIn(assertion);
      onSuccess(userCredential);
    } catch (err) {
      console.error('MFA verification error:', err);
      if (err.code === 'auth/invalid-verification-code') {
        setError('Invalid verification code. Please try again.');
      } else {
        setError(err.message || 'Verification failed. Please try again.');
      }
      setOtpCode('');
    } finally {
      setVerifying(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && otpCode.length === 6 && !verifying) {
      handleVerify();
    }
  };

  const handleCancel = () => {
    setOtpCode('');
    setError(null);
    onCancel();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Two-Factor Authentication</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter the 6-digit code from your authenticator app.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          fullWidth
          label="Verification Code"
          value={otpCode}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '');
            if (value.length <= 6) {
              setOtpCode(value);
            }
          }}
          onKeyDown={handleKeyDown}
          inputProps={{ maxLength: 6, inputMode: 'numeric', style: { letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.25rem' } }}
          placeholder="000000"
          disabled={verifying}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={verifying}>
          Cancel
        </Button>
        <Button
          onClick={handleVerify}
          variant="contained"
          disabled={otpCode.length !== 6 || verifying}
          startIcon={verifying ? <CircularProgress size={18} /> : null}
        >
          {verifying ? 'Verifying...' : 'Verify'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MfaChallengeDialog;
